import { getCookie } from '@alexis/helpers/cookie';
import { toISOStringWithTimezone } from '@alexis/helpers/date';
import { genzoTrack, genzoTrackPageView } from '@alexis/helpers/genzo';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { GlobalContext } from '@context/GlobalContext';

import { getNameForDataTracking } from '@helpers/flight';

import { getCurrentSiteState, getLocaleState, getUserState } from '@redux/selectors';

const useFlightResultGenzoTracking = ({
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
  pageViewId,
}: {
  wegoAnalyticsClientId: string | undefined;
  wegoAnalyticsClientSessionId: string | undefined;
  pageViewId: string;
}) => {
  const { webEngageAnonymousId } = useContext(GlobalContext);
  const { isPLSDepartLegSelection, isPerLegSearch } = useFlightPLS();
  const { search } = useFlightSearchResults();
  const { isMultiCity } = useFlightSearchPageParams();

  const currentSite = useSelector(getCurrentSiteState);
  const user = useSelector(getUserState);
  const locale = useSelector(getLocaleState);

  const [searchParams] = useSearchParams();
  const wegoSourceSearchParam = searchParams.get('wg_source');

  const [isGenzoFlightSearchTracked, setIsGenzoFlightSearchTracked] = useState(false);
  const [isGenzoPageViewTrackedForNonPLS, setIsGenzoPageViewTrackedForNonPLS] =
    useState<boolean>(false);
  const [isGenzoPageViewTrackedForPLSDepart, setIsGenzoPageViewTrackedForPLSDepart] =
    useState<boolean>(false);
  const [isGenzoPageViewTrackedForPLSReturn, setIsGenzoPageViewTrackedForPLSReturn] =
    useState<boolean>(false);
  const [isGenzoVisitsTracked, setIsGenzoVisitsTracked] = useState<boolean>(false);

  const isGenzoPageViewTracked = useMemo(() => {
    if (isPerLegSearch) {
      return isPLSDepartLegSelection
        ? isGenzoPageViewTrackedForPLSDepart
        : isGenzoPageViewTrackedForPLSReturn;
    }

    return isGenzoPageViewTrackedForNonPLS;
  }, [
    isGenzoPageViewTrackedForNonPLS,
    isGenzoPageViewTrackedForPLSDepart,
    isGenzoPageViewTrackedForPLSReturn,
    isPLSDepartLegSelection,
    isPerLegSearch,
  ]);

  // Genzo flight search and page view tracking
  useEffect(() => {
    if (
      !isGenzoPageViewTracked &&
      !!search?.id &&
      !!wegoAnalyticsClientId &&
      !!wegoAnalyticsClientSessionId &&
      !!webEngageAnonymousId
    ) {
      const { id, legs, adultsCount, childrenCount, infantsCount, cabin } = search;
      const now = new Date();

      //#region Genzo v3
      const clientData: any = {
        id: wegoAnalyticsClientId,
        session_id: wegoAnalyticsClientSessionId,
        ...(!!user && { user_hash: user.userHash }),
      };

      const name = getNameForDataTracking('Flights Results', legs);

      let subType = '';

      if (isPerLegSearch) {
        subType = isPLSDepartLegSelection ? 'per_leg_depart' : 'per_leg_return';
      } else {
        subType = `${isMultiCity ? 'multicity_' : ''}flights_search_results`;
      }

      const pageData: any = {
        name,
        product: 'flights',
        base_type: 'flights_search_results',
        sub_type: subType,
        locale,
        site_code: currentSite.countryCode,
        url: window.location.href,
        referrer_url: document.referrer,
      };

      const data: any = {
        id: pageViewId,
        client: clientData,
        event_id: id,
        page: pageData,
        created_at: toISOStringWithTimezone(new Date()),
        external_services: {
          crm_id: user ? user.userHash : webEngageAnonymousId,
        },
      };

      genzoTrackPageView(API_BASE_URL, data);
      //#endregion

      //#region Genzo v2
      const legsData = legs.map((leg, index) => {
        const legData: any = {
          order: index,
          outbound_date: leg.outboundDate,
          departure_city_code: leg.departureCity.code,
          departure_country_code: leg.departureCity.countryCode,
          arrival_city_code: leg.arrivalCity.code,
          arrival_country_code: leg.arrivalCity.countryCode,
        };

        if (leg.departureAirport) {
          legData.departure_airport_code = leg.departureAirport.code;
        }

        if (leg.arrivalAirport) {
          legData.arrival_airport_code = leg.arrivalAirport.code;
        }

        return legData;
      });

      const searchData: any = {
        id: id,
        user: user ? { email: user.email } : null,
        client: {
          ...clientData,
          ...{
            app_type: 'WEB_APP',
            resolution: `${window.screen.availHeight}x${window.screen.availWidth}`,
          },
        },
        campaign: {
          sub_id: id,
          source: wegoSourceSearchParam,
          ts_code: getCookie('wego_ts_code') ?? null,
        },
        page: { ...pageData },
        search: {
          id: id,
          cabin: cabin.toUpperCase(),
          adults_count: adultsCount,
          childrenCount: childrenCount,
          infants_count: infantsCount,
          legs: legsData,
        },
        created_at: now.toISOString(),
        search_mode: 'MANUAL',
      };
      delete searchData.page.product;
      delete searchData.page.sub_type;

      if (!isGenzoFlightSearchTracked) {
        genzoTrack(`${API_BASE_URL}/genzo/v2/flights/searches`, searchData);
        setIsGenzoFlightSearchTracked(true);
      }
      //#endregion

      if (isPerLegSearch) {
        if (isPLSDepartLegSelection) {
          setIsGenzoPageViewTrackedForPLSDepart(true);
        } else {
          setIsGenzoPageViewTrackedForPLSReturn(true);
        }
      } else {
        setIsGenzoPageViewTrackedForNonPLS(true);
      }
    }
  }, [
    currentSite.countryCode,
    isGenzoFlightSearchTracked,
    isGenzoPageViewTracked,
    isMultiCity,
    isPLSDepartLegSelection,
    isPerLegSearch,
    locale,
    pageViewId,
    search,
    user,
    webEngageAnonymousId,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
    wegoSourceSearchParam,
  ]);

  // Genzo visits tracking
  useEffect(() => {
    if (
      !isGenzoVisitsTracked &&
      !!search?.id &&
      !!wegoAnalyticsClientId &&
      !!wegoAnalyticsClientSessionId &&
      !!pageViewId &&
      !!webEngageAnonymousId
    ) {
      const { legs } = search;
      const { departureAirport, departureCity, arrivalAirport, arrivalCity } = legs[0];

      const visitData: any = {
        id: pageViewId,
        client: {
          app_type: 'WEB_APP',
          resolution: window.screen.availHeight + 'x' + window.screen.availWidth,
          id: wegoAnalyticsClientId,
          session_id: wegoAnalyticsClientSessionId,
        },
        campaign: { source: wegoSourceSearchParam, ts_code: getCookie('wego_ts_code') ?? null },
        page: {
          locale,
          site_code: currentSite.countryCode,
          url: window.location.href,
          referrer_url: document.referrer,
          name: `Flights Results|${departureAirport ? departureAirport.code : departureCity.code}|${
            arrivalAirport ? arrivalAirport.code : arrivalCity.code
          }|${departureCity.enName}, ${departureCity.countryEnName} to ${arrivalCity.enName}, ${
            arrivalCity.countryEnName
          }`,
          base_type: 'flights_search_results',
        },
        created_at: new Date().toISOString(),
      };

      if (user) {
        visitData.user = { email: user.email };
        visitData.client.user_hash = user.userHash;
        visitData.external_service = {
          crm_id: user.userHash,
        };
      } else {
        visitData.external_service = {
          crm_id: webEngageAnonymousId,
        };
      }

      genzoTrack(`${API_BASE_URL}/genzo/v2/visits`, visitData);

      setIsGenzoVisitsTracked(true);
    }
  }, [
    currentSite.countryCode,
    isGenzoVisitsTracked,
    locale,
    pageViewId,
    search,
    user,
    webEngageAnonymousId,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
    wegoSourceSearchParam,
  ]);
};

export default useFlightResultGenzoTracking;
